/**
 * Allys Project Detail Hero
 * 
 * A stunning full-screen hero section for project detail pages
 * with parallax effect, cinematic overlays, and elegant typography.
 */

'use client';

import { motion, useScroll, useTransform } from 'framer-motion';
import Image from 'next/image';
import { useRef } from 'react';

// Project interface - matches PropertyDevelopmentProject
interface ProjectData {
  id: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  projectType?: string | null;
  status?: string | null;
  stage?: string | null;
  totalUnits?: number | null;
  soldUnits?: number | null;
  availableUnits?: number | null;
  displayPriceText?: string | null;
  heroImageUrl?: string | null;
  estimatedCompletionDate?: string | null;
  marketingStatusText?: string | null;
}

export interface AllysProjectDetailHeroProps {
  project: ProjectData;
  brandColors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
  messages: {
    viewProject?: string;
    startingFrom?: string;
    units?: string;
    comingSoon?: string;
    availableNow?: string;
    soldOut?: string;
  };
  locale?: string;
}

/**
 * AllysProjectDetailHero - Cinematic full-screen hero for project pages
 */
export function AllysProjectDetailHero({
  project,
  brandColors,
  messages,
  locale = 'en',
}: AllysProjectDetailHeroProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Parallax scroll effect
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  
  // Allys theme colors
  const colors = {
    primary: brandColors?.primary || '#243462',
    secondary: brandColors?.secondary || '#040813',
    accent: brandColors?.accent || '#BBA339',
  };
  
  // Status helpers
  const isSoldOut = project.status === 'SOLD_OUT' || project.availableUnits === 0;
  const isComingSoon = project.status === 'UPCOMING';
  const isActive = project.status === 'ACTIVE' && !isSoldOut;
  
  // Format stage for display
  const formatStage = (stage: string | null): string => {
    if (!stage) return '';
    return stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  return (
    <div ref={containerRef} className="relative h-screen min-h-[700px] overflow-hidden">
      {/* Background Image with Parallax */}
      <motion.div 
        className="absolute inset-0"
        style={{ y, scale }}
      >
        {project.heroImageUrl ? (
          <Image
            src={project.heroImageUrl}
            alt={project.name}
            fill
            className="object-cover"
            priority
            sizes="100vw"
            quality={90}
          />
        ) : (
          <div
            className="h-full w-full"
            style={{
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 50%, ${colors.primary} 100%)`,
            }}
          />
        )}
        
        {/* Cinematic gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/30" />
        
        {/* Vignette effect */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, transparent 50%, rgba(0,0,0,0.4) 100%)',
          }}
        />
        
        {/* Grain texture overlay */}
        <div 
          className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
            backgroundSize: '180px 180px'
          }}
        />
      </motion.div>
      
      {/* Content */}
      <motion.div 
        className="relative z-10 flex h-full flex-col justify-end"
        style={{ opacity }}
      >
        <div className="mx-auto w-full max-w-7xl px-6 pb-16 lg:px-8 lg:pb-24">
          {/* Status Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-6 flex items-center gap-4"
          >
            {/* Project Type Badge */}
            {project.projectType && (
              <span 
                className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] backdrop-blur-md"
                style={{ backgroundColor: `${colors.accent}`, color: '#fff' }}
              >
                {project.projectType.replace(/_/g, ' ')}
              </span>
            )}
            
            {/* Status Badge */}
            {isSoldOut ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-red-600/90 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white backdrop-blur-md">
                {messages.soldOut || 'Sold Out'}
              </span>
            ) : isComingSoon ? (
              <span 
                className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white backdrop-blur-md"
                style={{ backgroundColor: colors.primary }}
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                </span>
                {messages.comingSoon || 'Coming Soon'}
              </span>
            ) : isActive ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-600/90 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white backdrop-blur-md">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                </span>
                {messages.availableNow || 'Available Now'}
              </span>
            ) : null}
          </motion.div>
          
          {/* Project Stage */}
          {project.stage && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mb-4 flex items-center gap-3"
            >
              <div 
                className="h-px w-12 flex-shrink-0"
                style={{ backgroundColor: colors.accent }}
              />
              <span 
                className="text-sm font-semibold uppercase tracking-[0.25em]"
                style={{ color: colors.accent }}
              >
                {formatStage(project.stage)}
              </span>
            </motion.div>
          )}
          
          {/* Project Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="mb-6 max-w-4xl text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl font-inter"
            style={{ 
              textShadow: '0 4px 60px rgba(0,0,0,0.5)',
            }}
          >
            {project.name}
          </motion.h1>
          
          {/* Description */}
          {project.description && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="mb-8 max-w-2xl text-lg leading-relaxed text-white/85 md:text-xl font-inter"
              style={{ 
                textShadow: '0 2px 20px rgba(0,0,0,0.4)',
              }}
            >
              {project.description}
            </motion.p>
          )}
          
          {/* Key Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex flex-wrap items-center gap-6 border-t border-white/20 pt-8 md:gap-10"
          >
            {/* Price */}
            {project.displayPriceText && (
              <div className="flex flex-col">
                <span className="mb-1 text-xs font-medium uppercase tracking-[0.2em] text-white/60">
                  {messages.startingFrom || 'Starting from'}
                </span>
                <span 
                  className="text-2xl font-bold md:text-3xl"
                  style={{ color: colors.accent }}
                >
                  {project.displayPriceText}
                </span>
              </div>
            )}
            
            {/* Divider */}
            {project.displayPriceText && project.totalUnits && (
              <div className="h-12 w-px bg-white/20" />
            )}
            
            {/* Total Units */}
            {project.totalUnits && (
              <div className="flex flex-col">
                <span className="mb-1 text-xs font-medium uppercase tracking-[0.2em] text-white/60">
                  {messages.units || 'Units'}
                </span>
                <span className="text-2xl font-bold text-white md:text-3xl">
                  {project.totalUnits}
                </span>
              </div>
            )}
            
            {/* Divider */}
            {project.totalUnits && project.availableUnits !== null && !isSoldOut && (
              <div className="h-12 w-px bg-white/20" />
            )}
            
            {/* Available Units */}
            {project.availableUnits != null && !isSoldOut && (
              <div className="flex flex-col">
                <span className="mb-1 text-xs font-medium uppercase tracking-[0.2em] text-white/60">
                  Available
                </span>
                <span 
                  className="text-2xl font-bold md:text-3xl"
                  style={{ color: project.availableUnits > 0 ? '#10b981' : colors.accent }}
                >
                  {project.availableUnits}
                </span>
              </div>
            )}
            
            {/* Completion Date */}
            {project.estimatedCompletionDate && (
              <>
                <div className="hidden h-12 w-px bg-white/20 md:block" />
                <div className="flex flex-col">
                  <span className="mb-1 text-xs font-medium uppercase tracking-[0.2em] text-white/60">
                    Completion
                  </span>
                  <span className="text-2xl font-bold text-white md:text-3xl">
                    {new Date(project.estimatedCompletionDate).toLocaleDateString(locale, { 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </motion.div>
      
      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-2"
        >
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-white/50">
            Scroll to explore
          </span>
          <svg 
            className="h-6 w-6 text-white/50" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </motion.div>
      </motion.div>
    </div>
  );
}
